import { Avatar, AccountCircleIcon, AccountCircleOutlinedIcon, PushPinOutlinedIcon, Badge, RetryImage, Box } from 'src/components';
import { useNetworkStatus } from 'src/hooks';

import placeholderImage from 'src/assets/placeholders/white-140x140.png';

const COLORS = ['#6C74DD', '#F27798', '#5EC6CC'];

const OfflineBadge = () => (
  <Box
    sx={{
      position: 'absolute',
      bottom: -4,
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: 'rgba(0,0,0,0.75)',
      color: 'white',
      fontSize: '9px',
      fontWeight: 700,
      px: 0.9,
      py: 0,
      borderRadius: 6,
      border: '1px solid rgba(255,255,255,0.25)',
      boxShadow: 'none',
      zIndex: 10,
      pointerEvents: 'none',
    }}
  >
    Offline
  </Box>
);

export default function UserAvatar({
  user,
  size = 40,
  isMobile = false,
  sx,
  isActiveTab = false,
  showPrivacyBadge = false,
  retryImage = false,
  retryImagePlaceholderDefault = false,
  retryImageShowLoader = false,
  showOfflineBadge = false,
  badgeColor = '#fff',
  ...otherProps
}) {
  const isLoggedIn = !!user;
  const online = useNetworkStatus();

  const initials = user?.name
    ? user.name
        .trim()
        .split(' ')
        .slice(0, 2)
        .map(n => n[0])
        .join('')
        .toUpperCase()
    : '?';

  const bgColor = isLoggedIn && !user.profileImageUrl ? getColorFromUser(user) : undefined;

  if (!isLoggedIn) {
    return isMobile ? (
      <>
        {isActiveTab ? (
          <AccountCircleIcon sx={{ fontSize: size }} {...otherProps} />
        ) : (
          <AccountCircleOutlinedIcon sx={{ fontSize: size }} {...otherProps} />
        )}
      </>
    ) : (
      <AccountCircleIcon sx={{ fontSize: size }} {...otherProps} />
    );
  }

  const renderAvatar = (content, avatarSx = {}) => {
    const avatarElement = (
      <Avatar
        sx={{
          width: size,
          height: size,
          fontSize: size * 0.5,
          bgcolor: bgColor,
          fontWeight: 'normal',
          color: 'white',
          ...avatarSx,
          ...sx,
        }}
        {...otherProps}
      >
        {!user.profileImageUrl ? content : (
          retryImage ? (
            <RetryImage
              src={user.profileImageUrl}
              placeholder={placeholderImage}
              placeholderDefault={retryImagePlaceholderDefault}
              showLoader={retryImageShowLoader}
              style={{ width: '100%', height: '100%', borderRadius: '50%' }}
            />
          ) : (
            <img
              src={user.profileImageUrl}
              alt={user.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '50%',
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )
        )}
      </Avatar>
    );

    const avatarWithBadge = (
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        {avatarElement}
        {!online && showOfflineBadge && <OfflineBadge />}
      </Box>
    );

    if (showPrivacyBadge && user?.privacy === 'Pinned') {
      return (
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <PushPinOutlinedIcon
              sx={{
                borderRadius: 50,
                bgcolor: badgeColor,
                fontSize: 14,
                p: '2px',
                color: badgeColor === '#fff' ? 'grey.600' : '#fff',
              }}
            />
          }
        >
          {avatarWithBadge}
        </Badge>
      );
    }

    return avatarWithBadge;
  };

  if (!user.profileImageUrl) {
    return renderAvatar(initials);
  }

  return renderAvatar(null, { bgcolor: undefined });
}

function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
}

function getColorFromUser(user) {
  if (!user) return '#757575';
  const key = user.id || user.email || user.name || 'default';
  const hash = hashCode(key.toString());
  const index = Math.abs(hash) % COLORS.length;
  return COLORS[index];
}
